import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Grid, Stack, Typography } from '@mui/material';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import CustomIconButton from 'common/components/CustomIconButton';
import CustomTooltip from 'common/components/CustomTooltip';
import YesNoDialog from 'common/components/YesNoDialog';
import { useSettingValue } from 'common/hooks/useSettingValue';
import * as moment from 'moment';
import { useSnackbar } from 'notistack';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getUser } from 'redux/slices/authSlice';
import { deleteAvailabilityAction } from 'redux/slices/iCalendarV4/availabilitySlice';
import { openNestedDialog } from 'redux/slices/nestedDialogSlice';
import 'suneditor/dist/css/suneditor.min.css';
import ServicesList from '../services/ServicesList';
import MyAvailabilityTable from './MyAvailabilityTable';
import MyAvailabilityEdit from './myAvailabilityForm/MyAvailabilityEdit';

const MyAvailabilityItem = ({ availability, userData, groups }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const overrides = availability.overrides;
    const unavailableDates = availability.unavailableDates;
    const user = useSelector(getUser);
    const enableAgentEditAvailability = useSettingValue(SettingsKey.ENABLE_AGENT_EDIT_AVAILABILITY) ?? true;
    const disableAgentEditAvailability = user.role === 'creator' && !enableAgentEditAvailability;
    const [openConfirmDeleteDlg, setOpenConfirmDeleteDlg] = useState(false);

    const titleDlg = t('myAvailability.myAvailabilityItem.deleteConfirm');
    const messageDlg = t('myAvailability.myAvailabilityItem.deleteMessage');

    const handleDelete = (e) => {
        e.stopPropagation();
        setOpenConfirmDeleteDlg(true);
    };

    const confirmDelete = () => {
        dispatch(
            deleteAvailabilityAction(availability.id, () => {
                enqueueSnackbar(t('myAvailability.myAvailabilityItem.deleteDone'), { variant: 'success' });
            }),
        );
        setOpenConfirmDeleteDlg(false);
    };

    const cancelDlg = () => {
        setOpenConfirmDeleteDlg(false);
    };

    const handleEdit = () => {
        dispatch(openNestedDialog(<MyAvailabilityEdit availability={availability} userData={userData} />));
    };

    return (
        <Fragment>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Typography variant="button" display="block" gutterBottom>
                    {availability.name}
                </Typography>

                {!disableAgentEditAvailability && (
                    <CustomTooltip arrow placement="bottom" title={t('actions.edit')} aria-label="edit availability">
                        <CustomIconButton id="onlive-button-edit" size="small" onClick={handleEdit}>
                            <EditOutlinedIcon component="svg" />
                        </CustomIconButton>
                    </CustomTooltip>
                )}

                {!disableAgentEditAvailability && (
                    <CustomTooltip
                        arrow
                        placement="bottom"
                        title={t('actions.delete')}
                        aria-label="delete availability"
                    >
                        <CustomIconButton id="onlive-button-edit" size="small" onClick={handleDelete}>
                            <DeleteOutlineOutlinedIcon component="svg" />
                        </CustomIconButton>
                    </CustomTooltip>
                )}
            </Stack>

            <Grid container spacing={10}>
                <Grid item md={9} sm={12}>
                    <Stack direction="column" spacing={2}>
                        <MyAvailabilityTable availability={availability} />
                        <ServicesList
                            services={availability?.services?.map((service) => service.service)}
                            isService={false}
                            groups={groups}
                            userView={userData?.userView}
                        />
                    </Stack>
                </Grid>

                <Grid item md={3} sm={12}>
                    <Stack direction="column" spacing={6}>
                        <Stack direction="column" spacing={1}>
                            <Typography variant="subtitle1" fontWeight={500}>
                                {t('calendar.setOutOfTheOffice')}
                            </Typography>
                            <Stack direction="column" spacing={1}>
                                {unavailableDates?.map((item, idx) => (
                                    <Stack key={idx} direction="row" spacing={2}>
                                        <Typography fontWeight={400} variant="subtitle2">
                                            {moment(item.startAt).format('DD / MM / YYYY')}
                                        </Typography>
                                        <Typography fontWeight={400} variant="subtitle2">
                                            {t('services.form.dateTo')}
                                        </Typography>
                                        <Typography fontWeight={400} variant="subtitle2">
                                            {moment(item.endAt).format('DD / MM / YYYY')}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>
                        <Stack direction="column" spacing={1}>
                            <Typography variant="subtitle1" fontWeight={500}>
                                <Box component="span" fontWeight="fontWeightMedium">
                                    {t('calendar.dateOverride')}
                                </Box>
                            </Typography>
                            <Stack direction="column" spacing={2}>
                                {Object.keys(overrides)?.map((key) => (
                                    <Stack direction="column" key={key} spacing={1}>
                                        <Typography variant="subtitle2" fontWeight={400}>
                                            {moment(key).format('DD / MM / YYYY')}
                                        </Typography>
                                        {overrides[key]?.map((hour, idx) => (
                                            <Stack key={idx} direction="row" spacing={2}>
                                                <Typography fontWeight={400} variant="subtitle2">
                                                    {hour.startTime} h
                                                </Typography>
                                                <Typography fontWeight={400} variant="subtitle2">
                                                    -
                                                </Typography>
                                                <Typography fontWeight={400} variant="subtitle2">
                                                    {hour.endTime} h
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>

            {/* <MyAvailabilityDetails availability={availability} /> */}
            {openConfirmDeleteDlg && (
                <YesNoDialog
                    openDialog={true}
                    title={titleDlg}
                    messageToConfirm={messageDlg}
                    yesButtonCallback={() => {
                        confirmDelete();
                    }}
                    noButtonCallback={() => {
                        cancelDlg();
                    }}
                />
            )}
        </Fragment>
    );
};

export default MyAvailabilityItem;
